import { computed } from 'vue'
import type { MaybeRefOrGetter } from 'vue'
import { toValue } from 'vue'

export type PinnedDirection = 'left' | 'right'

export interface PinnedColumnDef {
  pinned?: PinnedDirection
  pinnedWidth?: number
}

/**
 * Calcule l'ordre et les offsets cumulés pour une table à colonnes épinglées.
 * Renvoie des helpers à passer aux primitives `<TableHead :pinned-offset="...">`
 * et `<TableCell :pinned-offset="...">`.
 *
 * Hypothèse : si une colonne est `pinned`, elle DOIT déclarer une `pinnedWidth`
 * (en pixels) pour que les offsets soient calculés correctement. Sans largeur,
 * l'offset est 0 et les colonnes pinned se chevaucheront.
 */
export function usePinnedColumns<C extends PinnedColumnDef>(
  columns: MaybeRefOrGetter<Record<string, C>>,
  order: MaybeRefOrGetter<string[]>
) {
  const leftKeys = computed(() => toValue(order).filter((key) => toValue(columns)[key]?.pinned === 'left'))

  const rightKeys = computed(() => toValue(order).filter((key) => toValue(columns)[key]?.pinned === 'right'))

  const offsetFor = (key: string): number => {
    const cols = toValue(columns)
    const col = cols[key]
    if (!col?.pinned) return 0
    const keys = col.pinned === 'left' ? leftKeys.value : rightKeys.value
    const idx = keys.indexOf(key)
    if (idx === -1) return 0
    const before = col.pinned === 'left' ? keys.slice(0, idx) : keys.slice(idx + 1)
    return before.reduce((sum, k) => sum + (cols[k]?.pinnedWidth ?? 0), 0)
  }

  return {
    leftKeys,
    rightKeys,
    offsetFor
  }
}
